// ReservationsAggregatedCache.js
import Axios from "axios";
import { URLAPI } from "../configuration";

const DAY_MS = 24 * 60 * 60 * 1000;

export default class ReservationsAggregatedCache {
    constructor({
        headers,
        hotelIDs,
        dateBy = null,
        groupBy = [],
        groupByDay = true,
        maxUses = 8,
        fromBooking = null,
        toBooking = null,
        source = "reservations_aggregated",
    }) {
        this.URLAPI = URLAPI;
        this.headers = headers;
        this.hotelIDs = [...hotelIDs].map(Number).sort((a, b) => a - b);
        this.dateBy = dateBy;
        this.groupBy = [...groupBy].sort();
        this.groupByDay = groupByDay;
        this.maxUses = maxUses;
        this.fromBooking = fromBooking;
        this.toBooking = toBooking;
        this.source = source;

        this.rows = [];
        this.ranges = [];
        this.usesLeft = maxUses;
    }

    static getKey({
        hotelIDs,
        dateBy = null,
        groupBy = [],
        groupByDay = true,
        fromBooking = null,
        toBooking = null,
        source = "reservations_aggregated",
    }) {
        return JSON.stringify({
            hotelIDs: [...hotelIDs].map(Number).sort((a, b) => a - b),
            dateBy,
            groupBy: [...groupBy].sort(),
            groupByDay,
            fromBooking,
            toBooking,
            source,
        });
    }

    async get(from, to) {
        const missingRanges = this.getMissingRanges(from, to);

        for (const range of missingRanges) {
            await this.load(range.from, range.to);
        }

        if (missingRanges.length === 0) {
            this.usesLeft -= 1;
        }

        const data = this.getRows(from, to);

        return {
            status: "loaded",
            data,
            extra: this.getExtra(from, to, data),
            cached: missingRanges.length === 0,
            missingRanges,
        };
    }

    async load(from, to) {
        let responseData = [];

        if (this.source === "search_requests_dates_summary") {
            const { data } = await Axios.post(
                `${this.URLAPI}/search_requests/dates_summary`,
                {},
                {
                    headers: this.headers,
                    params: {
                        from,
                        to,
                        hotel_id: this.hotelIDs.join(","),
                        date_by: this.dateBy,
                    },
                },
            );

            responseData = data.data ?? [];
        } else {
            const { data } = await Axios.post(
                `${this.URLAPI}/reservations_aggregated`,
                {
                    hotelIDs: this.hotelIDs,
                    from,
                    to,
                    dateBy: this.dateBy,
                    groupBy: this.groupBy,
                    groupByDay: this.groupByDay,
                    ...(this.fromBooking
                        ? { fromBooking: this.fromBooking }
                        : {}),
                    ...(this.toBooking ? { toBooking: this.toBooking } : {}),
                },
                {
                    headers: this.headers,
                },
            );

            responseData = data.data ?? [];
        }

        this.rows = this.mergeRows([...this.rows, ...responseData]);

        this.ranges = this.mergeRanges([
            ...this.ranges,
            {
                from,
                to,
            },
        ]);

        this.usesLeft = this.maxUses;
    }

    getMissingRanges(from, to) {
        let cursor = this.toMs(from);
        const end = this.toMs(to);
        const missing = [];

        this.ranges
            .map((range) => ({
                from: this.toMs(range.from),
                to: this.toMs(range.to),
            }))
            .sort((a, b) => a.from - b.from)
            .forEach((range) => {
                if (range.to < cursor || range.from > end) return;

                if (range.from > cursor) {
                    missing.push({
                        from: this.fromMs(cursor),
                        to: this.fromMs(range.from - DAY_MS),
                    });
                }

                cursor = Math.max(cursor, range.to + DAY_MS);
            });

        if (cursor <= end) {
            missing.push({
                from: this.fromMs(cursor),
                to: this.fromMs(end),
            });
        }

        return missing;
    }

    getRows(from, to) {
        const fromDay = this.toDay(from);
        const toDay = this.toDay(to);

        return this.rows.filter((row) => {
            const day = this.toDay(this.getRowDate(row));
            return fromDay <= day && day <= toDay;
        });
    }

    mergeRanges(ranges) {
        return ranges
            .sort((a, b) => this.toMs(a.from) - this.toMs(b.from))
            .reduce((acc, range) => {
                const last = acc.at(-1);

                if (!last) {
                    acc.push({ ...range });
                    return acc;
                }

                if (this.toMs(range.from) <= this.toMs(last.to) + DAY_MS) {
                    last.to = this.fromMs(
                        Math.max(this.toMs(last.to), this.toMs(range.to)),
                    );
                } else {
                    acc.push({ ...range });
                }

                return acc;
            }, []);
    }

    mergeRows(rows) {
        const map = new Map();

        rows.forEach((row) => {
            map.set(this.getRowKey(row), row);
        });

        return Array.from(map.values()).sort(
            (a, b) =>
                this.toMs(this.getRowDate(a)) - this.toMs(this.getRowDate(b)),
        );
    }

    getRowKey(row) {
        if (this.source === "search_requests_dates_summary") {
            return this.toDay(row.date);
        }

        return [
            this.toDay(row.selectedDate),
            row.CancelStatus ?? "",
            row.Type ?? "",
            row.ResTypeID ?? "",
            ...this.groupBy.map((field) => row[field] ?? ""),
        ].join("|");
    }

    getRowDate(row) {
        return row.selectedDate ?? row.date;
    }

    getExtra(from, to, data) {
        return {
            arg_from: this.toDay(from),
            arg_to: this.toDay(to),
            arg_date_by: this.dateBy,
            hotelIDs: this.hotelIDs,
            groupBy: this.groupBy,
            groupByDay: this.groupByDay,
            isAggregated: true,
            ci_index: [],
            co_index: [],
            rd_index: [],
            last_ResDate: this.getLastDate(data, "ResDate"),
            last_CheckIn: this.getLastDate(data, "CheckIn"),
            last_CheckOut: this.getLastDate(data, "CheckOut"),
        };
    }

    getLastDate(data, field) {
        return (
            data
                .map((row) => this.toDay(row[field]))
                .filter(Boolean)
                .sort()
                .at(-1) ?? null
        );
    }

    toDay(value) {
        if (!value) return "";
        if (typeof value === "string") return value.slice(0, 10);
        return new Date(value).toISOString().slice(0, 10);
    }

    toMs(value) {
        return new Date(`${this.toDay(value)}T00:00:00.000Z`).getTime();
    }

    fromMs(ms) {
        return new Date(ms).toISOString().slice(0, 10);
    }
}
